import React, { useEffect, useState } from 'react';
import styled from 'astroturf/react';
import { Map } from 'immutable';

import routes from 'sitewide-js/core_pre_webpack/routes';
import fetchWithCsrf from 'js/utils/fetch_with_csrf';
import {
  DisconnectedCanonicalWorkResults as CanonicalWorkResults,
  Work,
} from './canonical_work_results';

type Props = {
  works: Work[];
  forceRullWidth?: boolean;
};
interface Suggestions {
  already_shared_user_ids: number[];
  suggestions: unknown[];
}

const csrfToken =
  document.querySelector("[name='csrf-token']")?.getAttribute('content') || '';

const Results = ({ works, forceRullWidth = false }: Props) => {
  const defaultContainer = styled('div')`
    grid-column: 2 / 4;
    width: 100%;
  `;

  const fullWidthContainer = styled('div')`
    grid-column: 1 / 4;
    width: 100%;
  `;

  const Container = forceRullWidth ? fullWidthContainer : defaultContainer;

  // This holds indiviual authors
  // and the data to show for their popovers:
  // views, works, and followers.
  const [actors, setActors] = useState({});
  // Used to show which works a user has bookmarked
  // key: work id
  // value: boolean, true for bookmarked, false for none
  // We have to keep track of the false values
  // to overwrite the search API's work's `isBookmarked` values
  const [bookmarks, setBookmarks] = useState(Map<number, boolean>());
  // Used to show which users a user is following
  // key: user id
  // value: boolean, true for following, false for not
  // The api doesn't return following values for authors,
  // so we could get away with just using a Set,
  // but we're using a Map for consistency, and it
  // should be easier in the future if the API _does_
  // return whether the current user is following the author
  const [following, setFollowing] = useState(Map<number, boolean>());
  // Used to show which research interests a user is following
  // key: ri id
  // value: boolean, true for following, false for not
  // The api doesn't return following values for ris,
  // so we could get away with just using a Set,
  // but we're using a Map for consistency, and it
  // should be easier in the future if the API _does_
  // return whether the current user is following the ri
  const [followingRis, setFollowingRis] = useState(Map<number, boolean>());
  // This holds state for how many followers a research interest has,
  // used in the research interest popover
  const [ris, setRis] = useState({});
  // Used to show suggestions of users to share a work with
  // key: work id
  // value: suggestions from api
  const [suggestions, setSuggestions] = useState(Map<number, Suggestions>());
  // Used to show the view count for each work.
  // Even though the work id is a number, the response parsed
  // from the server has the keys as strings
  // key: work id
  // value: view count
  const [views, setViews] = useState(Map<string, number>());

  // Fetch view counts for each work,
  // we'll merge the data in to the work objects later.
  // This response looks like:
  // where the keys are the work ids, and the values
  // are their view counts
  // {
  //   views: {
  //     123456: 100,
  //     234567: 501,
  //     345678: 650,
  //   }
  // }
  useEffect(() => {
    fetch(
      routes.api_v0_works_views_path({
        subdomain_param: 'api',
        work_ids: works.map((w) => w.id),
      }),
    )
      .then((res) => res.json())
      .then((data) => setViews(Map(data.views)));
  }, [works]);

  // Most of these callbacks get the whole entity,
  // some of them just receive an id.
  // Also, some of these callbacks are expected to take a second
  // callback argument that's called when the callback is done,
  // because some of the components deeper in the tree
  // hold their own state.
  //
  // These callbacks were in `actions.wjs.jsx` in the previous
  // version of the search page's redux store.
  // This component attempts to simplify that logic
  // which used to go something like:
  // call the action ->
  // which calls a service ->
  // which makes an api call ->
  // which dispatches a redux action with the updated state
  const addToLibrary = (id: number, source: string, cb: () => void) => {
    const params = {
      'bookmark[content_id]': id,
      'bookmark[content_type]': 'Work',
      'bookmark[source]': source ?? 'search',
      'bookmark[from_download_at]': null,
      subdomain_param: 'api',
    };

    fetch(routes.api_v0_bookmarks_path(params), {
      headers: {
        'X-CSRF-Token': csrfToken,
      },
      method: 'POST',
    })
      .then(() => setBookmarks((b) => b.set(id, true)))
      .then(cb);
  };

  // `actor` in this context is a user (the author)
  const fetchActorData = async ({ id }: { id: number }) => {
    // We create a new params object here because the `routes`
    // functions bungle the object if we try to reuse it between
    // calls 😑
    const params = () => ({
      user_id: id,
      subdomain_param: 'api',
    });

    const getFollowers = fetch(routes.api_v0_user_followers_path(params()));
    const getViews = fetch(routes.api_v0_user_stats_path(params()));
    const getWorks = fetch(routes.count_api_v0_user_works_path(params()));

    const responses = await Promise.all([getFollowers, getViews, getWorks]);
    const [followerData, viewData, workData] = await Promise.all(
      responses.map((res) => res.json()),
    );

    setActors((a) => ({
      ...a,
      [id]: {
        followerCount: followerData.meta.count,
        viewCount: viewData.all_time_stats.documents_count,
        workCount: workData.count,
      },
    }));
  };

  const fetchResearchInterestFollowerCount = async ({ id }: { id: number }) => {
    const params = {
      interest_id: id,
      subdomain_param: 'api',
    };

    fetch(routes.count_api_v0_interest_followers_path(params))
      .then((res) => res.json())
      .then((data) => data.count)
      .then((count) =>
        setRis((ris) => ({ ...ris, [id]: { followerCount: count } })),
      );
  };

  const followResearchInterest = ({ id }: { id: number }, cb: () => void) => {
    const params = {
      subdomain_param: 'api',
      'tag[id]': id,
      tag_type: 'ResearchInterest',
      target_type: 'User',
      // @ts-expect-error ts-migrate(2339) FIXME: Property 'User' does not exist on type 'typeof Aed... Remove this comment to see the full error message
      target_id: Aedu?.User?.current?.id,
    };

    fetch(routes.add_api_v0_taggings_path(params), {
      headers: { 'X-CSRF-Token': csrfToken },
      method: 'POST',
    })
      .then(() => setFollowingRis((r) => r.set(id, true)))
      .then(cb);
  };

  const followUser = (id: number, cb: () => void) => {
    const params = {
      user_id: id,
      subdomain_param: 'api',
    };

    fetch(routes.api_v0_user_following_index_path(params), {
      // CSRF token technically isn't required for this request,
      // but keeping it here to maintain consistency with unfollowUser
      headers: {
        'X-CSRF-Token': csrfToken,
      },
    })
      .then(() => setFollowing((f) => f.set(id, true)))
      .then(cb);
  };

  const loadWorkShareSuggestions = (id: number) => {
    const params = {
      subdomain_param: 'api',
      work_id: id,
    };

    fetch(routes.api_v0_suggestions_work_shares_path(params))
      .then((res) => res.json())
      // data here is a hash with two keys:
      // `suggestions` and `already_shared_user_ids`
      .then((data) => setSuggestions((s) => s.set(id, data)));
  };

  // Most of these callbacks get the whole entity,
  // this one just recieves the workId.
  // It's important to call `cb` here, because
  // `LibraryButtonWrapper` depends on it
  // to update its state
  const removeFromLibrary = (id: number, cb: () => void) => {
    const params = {
      content_id: id,
      subdomain_param: 'api',
    };

    fetch(routes.destroy_for_work_api_v0_bookmarks_path(params), {
      headers: { 'X-CSRF-Token': csrfToken },
      method: 'DELETE',
    })
      .then(() => setBookmarks((b) => b.set(id, false)))
      .then(cb);
  };

  // This doesn't have or update any state, just sends the requests
  const reportWork = (choices: string[], workId: number, category: string) => {
    // Create a RelatedWorkRejection for the given work
    fetchWithCsrf(
      routes.api_v0_related_work_rejections_path({ subdomain_param: 'api' }),
      {
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          source: 'search',
          work_id: workId,
          source_id: workId,
          for_work_id: null,
          category,
        }),
        method: 'POST',
      },
    );

    const surveyBody = JSON.stringify({
      choices,
      freeform: null,
      survey_type: 'search_work_rejection_survey',
      survey_version: 1,
    });
    // We create a new params object here because the `routes`
    // functions bungle the object if we try to reuse it between
    // calls 😑
    const params = () => ({
      subdomain_param: 'api',
    });

    // Submit multiple-choice survey results
    fetch(routes.api_v0_survey_submit_path(params()), {
      body: surveyBody,
      headers: { 'X-CSRF-Token': csrfToken },
      method: 'POST',
    });
  };

  // This doesn't have or update any state, just sends the request
  const requestJoinSession = (
    invitationToken: string,
    cb: (invitation_status: any) => void,
  ) => {
    const params = {
      discussion_id: invitationToken,
      source: 'news',
      subdomain_param: 'api',
    };

    fetch(routes.api_v0_discussion_audience_invitations_path(params))
      .then((res) => res.json())
      .then((data) => cb(data.invitation_status));
  };

  const unfollowResearchInterest = ({ id }: { id: number }, cb: () => void) => {
    const params = {
      subdomain_param: 'api',
      'tag[id]': id,
      tag_type: 'ResearchInterest',
      target_type: 'User',
      // @ts-expect-error ts-migrate(2339) FIXME: Property 'User' does not exist on type 'typeof Aed... Remove this comment to see the full error message
      target_id: Aedu?.User?.current?.id,
    };

    fetch(routes.remove_api_v0_taggings_path(params), {
      headers: { 'X-CSRF-Token': csrfToken },
      method: 'POST',
    })
      .then(() => setFollowingRis((r) => r.remove(id)))
      .then(cb);
  };

  const unfollowUser = (id: number, cb: () => void) => {
    const params = {
      user_id: id,
      subdomain_param: 'api',
    };

    fetch(routes.api_v0_user_following_index_path(params), {
      headers: {
        'X-CSRF-Token': csrfToken,
      },
      method: 'DELETE',
    })
      .then(() => setFollowing((f) => f.set(id, false)))
      .then(cb);
  };

  // Merge state of user actions with results from search api call
  // This is sort of messy, but basically for each set of actions
  // (follow/unfollow user, follow/unfollow ri, etc) and view counts,
  // they hold the id and value of the data in question
  // and we merge them here.
  works = works.map((w) => ({
    ...w,
    alreadySharedUserIds: suggestions.get(w.id)?.already_shared_user_ids,
    authors: w.authors.map((a) => ({ ...a, isFollowing: following.get(a.id) })),
    isBookmarked: bookmarks.get(w.id) ?? w.isBookmarked,
    researchInterests: w.researchInterests.map((r) => ({
      ...r,
      isFollowing: followingRis.get(r.id),
    })),
    shareSuggestions: suggestions.get(w.id)?.suggestions,
    // Have to cast to string first because the keys are strings,
    // but work ids are numbers
    views: views.get(String(w.id)),
  }));

  return (
    <Container>
      <CanonicalWorkResults
        actors={actors}
        addToLibrary={addToLibrary}
        fetchActorData={fetchActorData}
        fetchResearchInterestFollowerCount={fetchResearchInterestFollowerCount}
        followResearchInterest={followResearchInterest}
        followUser={followUser}
        loadWorkShareSuggestions={loadWorkShareSuggestions}
        // @ts-expect-error ts-migrate(2339) FIXME: Property 'User' does not exist on type 'typeof Aed... Remove this comment to see the full error message
        premium={Boolean(Aedu.User?.current?.premium)}
        removeFromLibrary={removeFromLibrary}
        reportWork={reportWork}
        requestJoinSession={requestJoinSession}
        researchInterests={ris}
        showStats
        unfollowResearchInterest={unfollowResearchInterest}
        unfollowUser={unfollowUser}
        works={works}
      />
    </Container>
  );
};

export default Results;
